import type { ErrorEvent } from "@sentry/react";

// "Contains" match on the normalized key, mirroring Sentry's own server-side scrubber semantics
// (e.g. "token" also catches accessToken, refreshToken, and legacy *_token names).
const SENSITIVE_KEY_TOKENS = [
	"authorization",
	"cookie",
	"secret",
	"apikey",
	"csrf",
	"xsrf",
	"token",
	"session",
	"password",
] as const;

const normalizeKey = (key: string): string => key.toLowerCase().replace(/[-_]/g, "");

const isSensitiveKey = (key: string): boolean => {
	const normalized = normalizeKey(key);
	return SENSITIVE_KEY_TOKENS.some((token) => normalized.includes(token));
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (typeof value !== "object" || value === null) return false;
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
};

/** Exported so callers can avoid handing the sanitizer trees it will refuse to walk. */
export const SANITIZE_MAX_DEPTH = 20;

// Reset tokens (64 chars) travel as URL path segments under innocent keys like request.url or
// breadcrumb data.from/to, so key-name matching can never catch them; scrub by shape instead.
const TOKEN_RUN_RE = /[A-Za-z0-9_-]{64,}/g;

export const scrubTokenRuns = (value: string): string => value.replace(TOKEN_RUN_RE, "[redacted]");

/**
 * Recursively deletes sensitive keys, rewrites token-shaped key names, and scrubs token-like string
 * values from an object/array tree in place. Only descends into plain objects and arrays, so Errors, Dates, DOM nodes, and other
 * class instances are left untouched.
 */
export const sanitizeInPlace = (value: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): void => {
	if (depth > SANITIZE_MAX_DEPTH) return;
	if (!Array.isArray(value) && !isPlainObject(value)) return;
	if (seen.has(value as object)) return;
	seen.add(value as object);

	if (Array.isArray(value)) {
		for (let ii = 0; ii < value.length; ii++) {
			const item = value[ii];
			if (typeof item === "string") {
				value[ii] = scrubTokenRuns(item);
				continue;
			}
			sanitizeInPlace(item, seen, depth + 1);
		}
		return;
	}

	for (const key of Object.keys(value)) {
		if (isSensitiveKey(key)) {
			delete value[key];
			continue;
		}

		// The property name itself can be the credential — a dictionary keyed by reset token, say.
		// Key names are only word-matched above, and scrubbing by shape otherwise reaches values
		// only, so rewrite the key too. Two keys that scrub to the same name collide and the last
		// one wins: acceptable for observability payloads, and it never leaks the original.
		let currentKey = key;
		const scrubbedKey = scrubTokenRuns(key);
		if (scrubbedKey !== key) {
			value[scrubbedKey] = value[key];
			delete value[key];
			currentKey = scrubbedKey;
		}

		if (typeof value[currentKey] === "string") {
			value[currentKey] = scrubTokenRuns(value[currentKey] as string);
			continue;
		}
		sanitizeInPlace(value[currentKey], seen, depth + 1);
	}
};

/**
 * Strips sensitive keys and token-like values from the whole Sentry event (request, headers,
 * breadcrumbs, contexts, tags, extra, user). Declared with just `event` — Sentry's `beforeSend`
 * type also passes a `hint`, but a function with fewer parameters still satisfies that type.
 */
export const beforeSendEvent = (event: ErrorEvent): ErrorEvent => {
	sanitizeInPlace(event);
	return event;
};
